import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import {
  DISPLAY_UNIT_ENUM,
  ALLOCATION_PERIOD_OVERLAP_TYPE
} from '~/modules/resourcing/common/enums';
import { deepPure } from '~/util';
import ResourceRequestUserAllocationPeriodOverlay, {
  ResourceRequestUserAllocationPeriodOverlayReadOnly
} from './ResourceRequestUserAllocationPeriodOverlay';
import ResourceAllocationHoursSummaryBlock from './ResourceAllocationHoursSummaryBlock';
import ResourceAllocationPercentageSummaryBlock from './ResourceAllocationPercentageSummaryBlock';
import {
  useStyles,
  useTimelineStyles,
  useOverlayStyles,
  useDragIndicatorStyles,
  getTimelineClassName
} from './GenericTimelineBlocks';

const useContainerStyles = makeStyles(() => ({
  root: {
    position: 'absolute',
    zIndex: 1
  },
  dragIndicators: {
    position: 'absolute',
    zIndex: 3
  },
  allocationOnTop: {
    zIndex: '2'
  },
  gridCellWrapper: {
    display: 'contents'
  }
}));

export const A11yGenericTimelineBlocks = ({
  allocation,
  handleAllocationPeriodClick,
  isEditable,
  resourceAvailabilitySummary,
  resourceRequest = {},
  scale,
  timeOffHolidaySummaryBlocksComponent,
  isReadOnly,
  displayUnit,
  overlayPeriods,
  allocationPeriods,
  dragIndicatorHandlers,
  hideCost,
  dataQeId
}) => {
  const {
    gestureBindEvents,
    dragDelta,
    currentResizeDirection: resizeDirection,
    dates: dragIndicatorDates
  } = dragIndicatorHandlers;
  const classes = useStyles();
  const timelineClasses = useTimelineStyles();
  const dragIndicatorClasses = useDragIndicatorStyles();
  const containerClasses = useContainerStyles();
  const overlayClasses = useOverlayStyles();

  const allocationMap = useMemo(
    () =>
      (allocationPeriods || []).reduce((map, allocationPeriod) => {
        map.set(allocationPeriod.key, allocationPeriod);

        return map;
      }, new Map()),
    [allocationPeriods]
  );

  const effectiveContainerClasses = useMemo(
    () => ({
      ...containerClasses,
      root: classNames(containerClasses.root, {
        [containerClasses.allocationOnTop]:
          displayUnit === DISPLAY_UNIT_ENUM.HOURS
      })
    }),
    [containerClasses, displayUnit]
  );

  const getAllocationSummaryComponent = allocationPeriod => {
    const {
      key,
      hasAllocationEnd,
      allocationPeriodOverlapType,
      isOverAllocated,
      isOverDistributed
    } = allocationPeriod;

    const timelineClassName = getTimelineClassName({
      allocation,
      timelineClasses,
      hasAllocationEnd,
      allocationPeriodOverlapType,
      isReadOnly,
      isOverAllocated,
      isOverDistributed
    });

    return displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE ? (
      <ResourceAllocationPercentageSummaryBlock
        key={`${key}-summary`}
        allocationPeriod={allocationPeriod}
        containerClasses={effectiveContainerClasses}
        dragIndicatorDates={dragIndicatorDates}
        dragDelta={dragDelta}
        dragIndicatorClasses={dragIndicatorClasses}
        gestureBindEvents={gestureBindEvents}
        resizeDirection={resizeDirection}
        showResizeIndicator={isEditable}
        timelineClassName={timelineClassName}
      />
    ) : (
      <ResourceAllocationHoursSummaryBlock
        key={`${key}-summary`}
        allocation={allocation}
        allocationPeriod={allocationPeriod}
        containerClasses={effectiveContainerClasses}
        dragIndicatorDates={dragIndicatorDates}
        dragDelta={dragDelta}
        dragIndicatorClasses={dragIndicatorClasses}
        gestureBindEvents={gestureBindEvents}
        handleAllocationPeriodClick={handleAllocationPeriodClick}
        overlayPeriods={overlayPeriods}
        allocationPeriods={allocationPeriods}
        resizeDirection={resizeDirection}
        resourceAvailabilitySummary={resourceAvailabilitySummary}
        resourceRequest={resourceRequest}
        scale={scale}
        showResizeIndicator={isEditable}
        timelineClassName={timelineClassName}
        hideCost={hideCost}
        dataQeId={dataQeId}
      />
    );
  };

  const getOverlayPeriodComponent = overlayPeriod => {
    if (isReadOnly) {
      if (
        displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE &&
        overlayPeriod.allocationPeriodOverlapType !==
          ALLOCATION_PERIOD_OVERLAP_TYPE.NONE
      )
        return (
          <ResourceRequestUserAllocationPeriodOverlayReadOnly
            key={`${overlayPeriod.key}-overlay`}
            overlayPeriod={overlayPeriod}
            allocation={allocation}
            resourceRequest={resourceRequest}
            requestPeriods={allocationPeriods}
          />
        );

      return null;
    }

    const {
      key,
      startDate,
      endDate,
      totalHours,
      totalScheduleHours,
      dynamicPosition,
      percentage,
      totalCost,
      allocationPeriodOverlapType
    } = overlayPeriod;

    return (
      <ResourceRequestUserAllocationPeriodOverlay
        key={`${key}-overlay`}
        startDate={startDate}
        endDate={endDate}
        totalHours={totalHours}
        requestPeriods={overlayPeriods}
        allocation={allocation}
        scale={scale}
        isEditable={isEditable}
        totalScheduleHours={totalScheduleHours}
        dynamicPosition={dynamicPosition}
        handleAllocationPeriodClick={handleAllocationPeriodClick}
        percentage={percentage}
        totalCost={totalCost}
        resourceRequest={resourceRequest}
        allocationPeriodOverlapType={allocationPeriodOverlapType}
        classes={overlayClasses}
        hideCost={hideCost}
      />
    );
  };

  return (
    <div className={`${classes.timeItem} resourceRequestUserAllocation`}>
      {timeOffHolidaySummaryBlocksComponent}
      {overlayPeriods.map(overlayPeriod => {
        const { key } = overlayPeriod;
        const components = [];

        if (allocationMap.has(key)) {
          components.push(
            getAllocationSummaryComponent(allocationMap.get(key))
          );
        }
        components.push(getOverlayPeriodComponent(overlayPeriod));

        return (
          <div
            key={`${key}-gridcell`}
            role="gridcell"
            tabIndex="-1"
            className={containerClasses.gridCellWrapper}
          >
            {components}
          </div>
        );
      })}
    </div>
  );
};

A11yGenericTimelineBlocks.propTypes = {
  scale: PropTypes.string.isRequired,
  allocation: PropTypes.object,
  handleAllocationPeriodClick: PropTypes.func,
  isEditable: PropTypes.bool,
  timeOffHolidaySummaryBlocksComponent: PropTypes.node,
  resourceRequest: PropTypes.object,
  resourceAvailabilitySummary: PropTypes.object,
  isReadOnly: PropTypes.bool,
  displayUnit: PropTypes.string,
  overlayPeriods: PropTypes.array,
  allocationPeriods: PropTypes.array,
  dragIndicatorHandlers: PropTypes.object,
  hideCost: PropTypes.bool,
  dataQeId: PropTypes.string
};

export default deepPure(A11yGenericTimelineBlocks);
